/**
 * 화면 색 토큰 (19회차, 2026-09-07 결정 14).
 *
 * 애플 공식 홈페이지를 참고한 라이트 테마다. 다크 테마는 두지 않는다.
 * 값의 원본은 `index.css`의 `@theme` 블록이고, 여기 있는 것은 **같은 값을 코드에서 볼 수 있게**
 * 적어 둔 사본이다. 캔버스(X-View)는 CSS 클래스를 못 쓰므로 색 문자열이 필요하고,
 * 대비 검사(`theme.test.ts`)도 숫자를 봐야 한다. 둘이 어긋나지 않게 `index.css`를 고치면
 * 여기도 같이 고친다(테스트가 값의 대비만 지킨다).
 *
 * 상태색은 애플 시스템 색 그대로가 아니라 **접근성 변형(어두운 쪽)**을 쓴다.
 * `#34c759`(초록)·`#ff9f0a`(주황)·`#ff3b30`(빨강)은 흰 바탕에서 2~3:1이라 글자로 못 쓴다.
 * 밝은 원색은 흰 글자를 얹는 채움 배경에서만 쓴다.
 */

/** 바탕과 글자 */
export const NEUTRAL = {
  /** 페이지 바탕 */
  canvas: '#ffffff',
  /** 카드 바탕 */
  panel: '#ffffff',
  /** 연회색 구획. 표 머리글·통계 타일·코드 블록·행 강조 */
  band: '#f5f5f7',
  /** 구획을 한 단계 더 누른 것. 눌린 단추·행 hover */
  bandStrong: '#ebebf0',
  /** 본문 글자 */
  ink: '#1d1d1f',
  /** 보조 글자·라벨·표 머리글 */
  muted: '#6e6e73',
  /** 구조를 나누는 테두리. 입력칸·단추·표 바깥선·머리글 밑줄 */
  line: '#86868b',
  /** 장식 실선. 표 행 사이처럼 위아래 바탕이 같은 자리에만 쓴다 */
  hair: '#d2d2d7',
} as const;

/**
 * 강조. 채움은 `accent`(위에 흰 글자), 글자로 쓸 때는 `link`.
 *
 * 애플 홈페이지의 hover는 원래 더 밝은 `#0077ed`인데, 그러면 그 위의 흰 글자가 4.3:1로
 * 떨어진다. 단추 위 글자가 hover에서만 읽기 어려워지면 안 되므로 어두운 쪽으로 눌렀다.
 */
export const ACCENT = {
  accent: '#0071e3',
  accentHover: '#0058b0',
  link: '#0066cc',
} as const;

/** 3단계 상태. 캔버스 점과 글자가 같은 색을 쓴다 */
export const STATUS = {
  ok: '#1d7a33',
  fail: '#c93400',
  error: '#d70015',
} as const;

/** 트랜잭션 타입 색. X-View 점·칩·범례가 같은 값을 본다 */
export const TX_COLOR = {
  http: '#0066cc',
  sse: '#8944ab',
  mcp: '#00726b',
  worker: '#7f6545',
  pdf: '#a2258f',
  /** 오래 걸리는 요청. 흰 바탕과 구획 바탕의 글자 대비를 함께 검사한다 */
  slow: '#b4531d',
} as const;

/** X-View 캔버스 전용. 밝은 바탕 위 팔레트다 */
export const CANVAS = {
  background: '#ffffff',
  /** 옅은 격자 */
  grid: '#e8e8ed',
  /** 눈금 글자 */
  tick: '#6e6e73',
  /** 오른쪽 끝의 지금 시각 선 */
  now: '#0071e3',
  selectionLine: '#0071e3',
  selectionFill: 'rgba(0, 113, 227, 0.12)',
} as const;

/** sRGB 한 성분을 상대 휘도 성분으로 (WCAG 2.1) */
function channel(value: number): number {
  const c = value / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

/** `#rrggbb`의 상대 휘도 */
export function luminance(hex: string): number {
  const r = Number.parseInt(hex.slice(1, 3), 16);
  const g = Number.parseInt(hex.slice(3, 5), 16);
  const b = Number.parseInt(hex.slice(5, 7), 16);
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** 두 색의 명암비. 1(같은 색)에서 21(검정과 흰색)까지 */
export function contrastRatio(a: string, b: string): number {
  const first = luminance(a);
  const second = luminance(b);
  const high = Math.max(first, second);
  const low = Math.min(first, second);
  return (high + 0.05) / (low + 0.05);
}
